/**
 * Session Warning Component
 * Shows a warning when session is about to expire and allows extension
 */

import { useState, useEffect } from 'react';
import { 
  isSessionAboutToExpire, 
  getSessionTimeRemaining, 
  extendSession, 
  handleSessionExpiry 
} from '@/utils/sessionManager';

const SessionWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    const checkSessionStatus = () => {
      const aboutToExpire = isSessionAboutToExpire();
      const remaining = getSessionTimeRemaining();
      
      setShowWarning(aboutToExpire);
      setTimeRemaining(remaining);
      
      // If session has expired, handle it
      if (remaining === 0 && aboutToExpire) {
        handleSessionExpiry();
      }
    };

    // Check immediately
    checkSessionStatus();

    // Set up interval to check every 30 seconds
    const interval = setInterval(checkSessionStatus, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleExtendSession = () => {
    extendSession();
    setShowWarning(false);
    console.log('Session extended by user');
  };

  const handleLogout = () => {
    handleSessionExpiry();
  };

  if (!showWarning) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-white p-4 shadow-lg">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <svg className="w-6 h-6 text-yellow-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="font-semibold">Session Expiring Soon</p>
            <p className="text-sm">
              Your session will expire in {timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}. 
              Extend your session to continue booking.
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExtendSession}
            className="bg-white text-yellow-600 px-4 py-2 rounded-md font-medium hover:bg-yellow-50 transition-colors"
          >
            Extend Session
          </button>
          <button
            onClick={handleLogout}
            className="bg-yellow-600 text-white px-4 py-2 rounded-md font-medium hover:bg-yellow-700 transition-colors"
          >
            Logout
          </button>
          <button
            onClick={() => setShowWarning(false)}
            className="text-yellow-200 hover:text-white transition-colors"
            aria-label="Close warning"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarning;
